import { alpha } from "@mui/material";
import {
  amber,
  blue,
  cyan,
  deepOrange,
  green,
  grey,
  lightGreen,
  pink,
  purple,
  red,
} from "@mui/material/colors";

export const CUSTOM_THEME_LIGHT = {
  // Theme mode
  mode: "light",

  // Background colors
  background: alpha(grey["A200"], 1),
  trackBackground: grey[50],
  trackBackgroundDragging: alpha(blue[900], 0.15),
  rulerBackground: grey[50],
  minimapBackground: alpha(grey[900], 0.95),
  minimapTrackBackground: alpha(grey[700], 0.75),

  // Grid
  gridColor: alpha(grey[900], 0.1),
  gridLineWidth: 1,

  // Waveform
  defaultWaveformColor: blue[900],
  waveformLineWidth: 1,
  inaudibleTrackOpacity: 0.3,
  waveformGainScaling: true,
  waveformGainCurve: "soft",

  spectrogramColormap: "gray",
  spectrogramSmoothing: true,
  spectrogramDisplayFloor: -90,
  spectrogramDisplayCeiling: -20,

  bufferedBarHeight: 3,
  bufferedBarColor: alpha(green[500], 0.85),
  stalledBarColor: alpha(amber[600], 0.9),

  // Track border (when dragging)
  trackBorderColor: blue[900],
  trackBorderWidth: 2,

  // Track labels
  trackLabelColor: grey[900],
  trackLabelFont: "12px sans-serif",
  trackLabelFontWeight: "normal",

  // Track offset display (when dragging)
  offsetDisplayBackground: grey[900],
  offsetDisplayColor: lightGreen["A400"],
  offsetDisplayFont: "12px monospace",
  offsetDisplayFontWeight: "normal",

  // Loading indicator
  loadingTextColor: grey[900],
  loadingTextFont: "14px sans-serif",
  loadingTextFontWeight: "bold",

  // Timeline ruler
  rulerTickColor: grey[700],
  rulerTextColor: grey[900],
  rulerTextFont: "10px monospace",
  rulerTextFontWeight: "normal",
  rulerMajorTickHeight: 38,
  rulerMinorTickHeight: 30,
  rulerTickBottomMargin: 22, // Margin from bottom of canvas where ticks start

  // Playhead
  playheadColor: red["A400"],
  playheadWidth: 2,

  // Minimap
  minimapWaveformOpacity: 0.6,
  minimapViewportFill: alpha(grey["A100"], 0.15),
  minimapViewportStroke: lightGreen[800],
  minimapViewportStrokeWidth: 2,
  minimapPlayheadColor: red["A400"],
  minimapPlayheadWidth: 1,

  // Scrollbar (legacy properties for backward compatibility)
  scrollbarTrackColor: grey[300],
  scrollbarThumbColor: grey[100],
  scrollbarThumbColorActive: grey[200],
  scrollbarThumbBorder: alpha(grey[900], 0.3),
  scrollbarWidth: 16,

  // Scrollbar configuration object (recommended)
  scrollbar: {
    width: 16, // Thickness of scrollbars (vertical width, horizontal height)
    minThumbSize: 40, // Minimum thumb size in pixels
    trackColor: grey[300],
    thumbColor: grey[100],
    thumbColorActive: grey[200],
    thumbBorder: alpha(grey[900], 0.3),
    showVertical: true,
    showHorizontal: true,
    customDrawFunction: null, // Optional: (ctx, geometry, isActive, theme, orientation) => void
  },

  scales: {
    background: "transparent",
    textColor: grey[900],
    centerTextColor: grey[900],
    tickColor: grey[800],
    minorTickColor: grey[800],
    centerLineColor: grey[900],
    font: "10px system-ui, sans-serif",
    majorTickLength: 8,
    minorTickLength: 4,
    labelGap: 4,
    tickAlign: "left",
    padding: 2,
    minimumLabelGap: 1.6,
    minimumTickGap: 3,
  },

  // Selection menu
  selectionMenu: {
    background: grey[50],
    border: "transparent",
    boxShadow:
      "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
    fontSize: "14px",
    color: grey[900],
    textColor: grey[900],
    readOnlyTextColor: grey[700],
    mouseEnterBackground: alpha(grey[300], 0.7),
    mouseLeaveBackground: "transparent",
    separatorBackground: alpha(grey[600], 0.9),
    detailsTextColor: grey[900],
    detailsFontSize: "12px",
  },

  // Selection
  selectionFill: alpha(amber["A400"], 0.2),
  selectionBorder: grey[900],
  selectionBorderWidth: 1,
  selectionHandleColor: amber["A400"],
  selectionHandleWidth: 8,
  selectionFillOpacity: 0.2,
  regionGroupColors: [
    amber[500],
    green[500],
    pink[500],
    blue[500],
    deepOrange[500],
    purple[500],
    cyan[500],
    lightGreen[500],
  ],
  inactiveRegionGroupOpacity: 0.35,
  regionGroupBarHeight: 6,
  regionGroupBarPosition: "top",
};

const defaultTheme = {
  background: "gray",
  trackBackground: "#f1f1f1",
  trackBackgroundDragging: "rgba(33, 150, 243, 0.2)",
  rulerBackground: "#2c2c2c",
  minimapBackground: "#1a1a1a",
  minimapTrackBackground: "#404040",

  gridColor: "rgba(255, 255, 255, 0.1)",
  gridLineWidth: 1,

  defaultWaveformColor: "#2196F3",
  waveformLineWidth: 1,
  inaudibleTrackOpacity: 0.3,
  waveformGainScaling: true,
  waveformGainCurve: "soft",

  spectrogramColormap: "magma",
  spectrogramSmoothing: true,
  spectrogramDisplayFloor: -90,
  spectrogramDisplayCeiling: -20,

  bufferedBarHeight: 3,
  bufferedBarColor: "rgba(76, 175, 80, 0.85)",
  stalledBarColor: "rgba(224, 164, 88, 0.9)",

  trackBorderColor: "#2196F3",
  trackBorderWidth: 2,

  trackLabelColor: "#333",
  trackLabelFont: "12px sans-serif",

  offsetDisplayBackground: "rgba(0, 0, 0, 0.7)",
  offsetDisplayColor: "#4CAF50",
  offsetDisplayFont: "12px monospace",

  loadingTextColor: "#333",
  loadingTextFont: "bold 14px sans-serif",

  rulerTickColor: "#666",
  rulerTextColor: "#ccc",
  rulerTextFont: "10px monospace",
  rulerMajorTickHeight: 38,
  rulerMinorTickHeight: 30,
  rulerTickBottomMargin: 22,

  playheadColor: "#FF4081",
  playheadWidth: 2,

  minimapWaveformOpacity: 0.6,
  minimapViewportFill: "rgba(255, 255, 255, 0.15)",
  minimapViewportStroke: "#4CAF50",
  minimapViewportStrokeWidth: 2,
  minimapPlayheadColor: "#FF4081",
  minimapPlayheadWidth: 1,

  scrollbarTrackColor: "rgba(0, 0, 0, 0.15)",
  scrollbarThumbColor: "rgba(255, 255, 255, 0.4)",
  scrollbarThumbColorActive: "rgba(255, 255, 255, 0.7)",
  scrollbarThumbBorder: "rgba(0, 0, 0, 0.3)",
  scrollbarWidth: 16,

  scrollbar: {
    width: 16,
    minThumbSize: 40,
    trackColor: "rgba(0, 0, 0, 0.15)",
    thumbColor: "rgba(255, 255, 255, 0.4)",
    thumbColorActive: "rgba(255, 255, 255, 0.7)",
    thumbBorder: "rgba(0, 0, 0, 0.3)",
    showVertical: true,
    showHorizontal: true,
    customDrawFunction: null,
  },

  scales: {
    background: "transparent",
    textColor: "#1f1f1f",
    centerTextColor: "#000000",
    tickColor: "#2e2e2e",
    minorTickColor: "#4a4a4a",
    centerLineColor: "#111111",
    font: "10px system-ui, sans-serif",
    majorTickLength: 8,
    minorTickLength: 4,
    labelGap: 4,
    tickAlign: "left",
    padding: 2,
    minimumLabelGap: 1.6,
    minimumTickGap: 3,
  },

  selectionMenu: {
    background: "#2c2c2c",
    border: "#444",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
    color: "#fff",
    fontSize: "13px",
    textColor: "#fff",
    readOnlyTextColor: "#666",
    mouseEnterBackground: "#404040",
    mouseLeaveBackground: "transparent",
    separatorBackground: "#444",
    detailsTextColor: "#999",
    detailsFontSize: "11px",
  },

  selectionBorder: "#FFC107",
  selectionBorderWidth: 2,
  selectionHandleWidth: 8,
  selectionFillOpacity: 0.2,
  regionGroupColors: [
    "#FFC107",
    "#4CAF50",
    "#E91E63",
    "#2196F3",
    "#FF5722",
    "#9C27B0",
    "#00BCD4",
    "#8BC34A",
  ],
  inactiveRegionGroupOpacity: 0.35,
  regionGroupBarHeight: 6,
  regionGroupBarPosition: "top",
};
